import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Scale, 
  ShieldCheck, 
  FileText, 
  CheckCircle2, 
  ExternalLink, 
  Compass, 
  Lock, 
  Mail, 
  Globe2,
  Cpu,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import EditorialHero from '@/components/ui/EditorialHero';
import { Content, Language } from '@/types';

interface TermsOfServiceProps {
  content: Content;
  lang: Language;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ content, lang }) => {
  const isAr = lang === 'ar';
  const isRtl = isAr;
  const data = content.legal.terms;

  const [activeSectionIdx, setActiveSectionIdx] = useState<number>(0);

  return (
    <PageTransition>
      <SEO 
        title={isAr ? "شروط الخدمة والحوسبة المكانية | منظومة فكسل" : "Terms of Service & Spatial Compute Agreement | VEXL Ecosystem"}
        description={
          isAr 
            ? "شروط الخدمة الرسمية واتفاقية الحوسبة المكانية لمنظومة فكسل المسجلة برقم 5007 لدى الجهاز المصري للملكية الفكرية."
            : "Official Terms of Service and Spatial Compute Agreement for VEXL Ecosystem (EGIPA Deposit #5007)."
        }
        lang={lang}
      />

      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden selection:bg-[#0E93A3]/30 ${
          isRtl ? 'font-arabic text-right' : 'font-sans text-left'
        }`} 
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 pt-20 md:pt-24 pb-24">
          
          {/* Canonical Two-Column Editorial Split Hero */}
          <EditorialHero
            title={data.title}
            description={
              isAr
                ? 'الإطار القانوني والتعاقدي الحاكم لاستخدام محركات فكسل الذكية، ونظام تشغيل VEXL Nova، وواجهات برمجة التطبيقات المكانية، وعقود الحوسبة السحابية والمحلية المعزولة.'
                : 'The official contractual framework governing the utilization of VEXL GeoAI engines, VEXL Nova OS, spatial APIs, and enterprise air-gapped compute agreements.'
            }
            actions={[
              { 
                label: isAr ? 'وثيقة الخصوصية' : 'Privacy Policy', 
                href: '/privacy', 
                variant: 'secondary' 
              },
              { 
                label: isAr ? 'الملكية الفكرية (EGIPA)' : 'IP Rights (EGIPA)', 
                href: '/intellectual-property', 
                variant: 'secondary' 
              }
            ]}
            isAr={isAr}
            className="mb-12"
          />

          {/* Main Layout: Sticky Sidebar TOC + Content Blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Desktop Table of Contents Sidebar */}
            <aside className="lg:col-span-4 hidden lg:block sticky top-36 space-y-4">
              <div className="p-5 rounded-2xl bg-[#12161A]/90 border border-[#262C31] backdrop-blur-md">
                <p className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold mb-4 flex items-center gap-2">
                  <FileText size={14} />
                  {isAr ? 'فهرس بنود الاتفاقية' : 'Table of Articles'}
                </p>
                <nav className="space-y-1.5">
                  {data.sections.map((section, idx) => (
                    <a
                      key={idx}
                      href={`#article-${idx + 1}`}
                      onClick={() => setActiveSectionIdx(idx)}
                      className={`block px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeSectionIdx === idx
                          ? 'bg-[#0E93A3]/15 text-[#0E93A3] border-r-2 border-[#0E93A3] font-bold'
                          : 'text-[#94A3B8] hover:text-white hover:bg-white/[0.04]'
                      }`}
                    >
                      {section.heading}
                    </a>
                  ))}
                </nav>

                <div className="mt-6 pt-5 border-t border-[#262C31]">
                  <Link
                    to="/intellectual-property"
                    className="flex items-center justify-between text-xs text-[#94A3B8] hover:text-[#0E93A3] transition-colors group"
                  >
                    <span className="flex items-center gap-2">
                      <ShieldCheck size={14} className="text-[#0E93A3]" />
                      {isAr ? 'بيانات الإيداع الفكري #5007' : 'EGIPA Patent Registry'}
                    </span>
                    <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Legal Support Box */}
              <div className="p-5 rounded-2xl bg-[#090C10] border border-[#262C31] space-y-3">
                <p className="text-xs font-bold text-white flex items-center gap-2">
                  <Mail size={14} className="text-[#0E93A3]" />
                  {isAr ? 'التواصل والاتفاقيات المؤسسية' : 'Institutional Legal Desk'}
                </p>
                <p className="text-xs text-[#94A3B8] leading-relaxed">
                  {isAr
                    ? 'لتوقيع اتفاقيات مستوى الخدمة (SLA) أو الاستفسار عن الشروط المخصصة:'
                    : 'For institutional SLA negotiation, custom enterprise agreements, or compliance:'}
                </p>
                <a
                  href="mailto:vexl.gis@gmail.com"
                  className="inline-flex items-center gap-2 text-xs font-mono text-[#0E93A3] hover:underline font-semibold"
                >
                  vexl.gis@gmail.com
                  <ExternalLink size={12} />
                </a>
              </div>
            </aside>

            {/* Articles Stream */}
            <main className="lg:col-span-8 space-y-6">
              {data.sections.map((section, idx) => (
                <section
                  id={`article-${idx + 1}`}
                  key={idx}
                  className="p-6 sm:p-8 rounded-2xl bg-[#12161A]/70 border border-[#262C31] hover:border-[#262C31]/90 transition-all backdrop-blur-sm"
                >
                  <div className="flex items-center justify-between gap-4 mb-4 pb-3 border-b border-[#262C31]/60">
                    <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2.5">
                      <span className="text-[#0E93A3] font-mono text-sm font-semibold">
                        §{idx + 1}.0
                      </span>
                      {section.heading}
                    </h2>
                    <span className="text-[10px] font-mono text-[#64748B] uppercase tracking-widest hidden sm:inline">
                      LEGAL BINDING
                    </span>
                  </div>

                  <div className="space-y-4">
                    {section.content.map((paragraph, pIdx) => (
                      <p 
                        key={pIdx} 
                        className="text-[#94A3B8] text-sm sm:text-base leading-relaxed font-normal"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              {/* Execution & Signature Endblock */}
              <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-[#12161A] to-[#0E93A3]/5 border border-[#262C31] mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {isAr ? 'جاهزية التعاقد المؤسسي والتشغيل' : 'Institutional Sovereign Execution'}
                  </h3>
                  <p className="text-xs text-[#94A3B8]">
                    {isAr 
                      ? 'وثيقة قانونية سارية ومسجلة طبقاً للقوانين المصرية والاتفاقيات الدولية للملكية الفكرية.'
                      : 'Governed by the laws of Egypt and registered under Egyptian Intellectual Property Authority (EGIPA).'}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Link
                    to="/privacy"
                    className="px-4 py-2 rounded-full bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3] text-xs font-semibold text-white transition-all"
                  >
                    {isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}
                  </Link>
                  <Link
                    to="/security"
                    className="px-4 py-2 rounded-full bg-[#0E93A3] text-black text-xs font-bold hover:bg-[#0bb2c6] transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)]"
                  >
                    {isAr ? 'معمارية الأمان' : 'Security Architecture'}
                  </Link>
                </div>
              </div>

            </main>

          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default TermsOfService;
